/**
 * Compliance Officer / Auditor landing — /app/landing/compliance
 *
 * CIS/NIST scorecards, attestation campaigns, GDPR purges, audit log,
 * ChangeProposal evidence. All data fetched from existing endpoints.
 */
import { useEffect, useState, useCallback } from 'react'
import {
  Scale, ClipboardCheck, GitPullRequest, RefreshCw,
} from 'lucide-react'
import { api } from '../lib/api'
import {
  LandingHeader, HeroTile, TwoColumnBody, Panel, EmptyState,
  StickyFooter, ListRow, safeNum,
} from '../components/landing/LandingShell'

interface Scorecard { framework: string; percent?: number; passing?: number; total?: number }
interface AttestItem { id: string; title?: string; due_at?: string; framework?: string }
interface AutoTicket { id: string; title?: string; framework?: string; rule?: string; severity?: string }
interface AuditEvent { id: string; action: string; actor?: string; created_at?: string }
interface RegTimer { id: string; framework?: string; due_at?: string }

export default function ComplianceLandingPage() {
  const [refreshing, setRefreshing]     = useState(false)
  const [cisCard, setCisCard]           = useState<Scorecard | null>(null)
  const [nistCard, setNistCard]         = useState<Scorecard | null>(null)
  const [attestQueue, setAttestQueue]   = useState<AttestItem[]>([])
  const [purgesPending, setPurges]      = useState<number>(0)
  const [campaigns, setCampaigns]       = useState<number>(0)
  const [doraTimers, setDoraTimers]     = useState<RegTimer[]>([])
  const [tickets, setTickets]           = useState<AutoTicket[]>([])
  const [audit, setAudit]               = useState<AuditEvent[]>([])
  const [openFindings, setFindings]     = useState<number>(0)
  const [proposals, setProposals]       = useState<number>(0)

  const load = useCallback(() => {
    setRefreshing(true)
    const j = (p: Promise<any>) => p.catch(() => ({ data: null }))
    Promise.all([
      j(api.get('/api/cdm/compliance/scorecard', { params: { framework: 'CIS-v8' } })),
      j(api.get('/api/cdm/compliance/scorecard', { params: { framework: 'NIST-CSF' } })),
      j(api.get('/api/cdm/attestation/me')),
      j(api.get('/api/cdm/privacy/purge-audit', { params: { state: 'pending' } })),
      j(api.get('/api/cdm/regulation-timers', { params: { state: 'pending_initial' } })),
      j(api.get('/api/cdm/auto-ticket/tickets', { params: { state: 'open' } })),
      j(api.get('/api/cdm/audit', { params: { limit: 5 } })),
      j(api.get('/api/cdm/changes/proposals', { params: { state: 'awaiting_approval' } })),
    ]).then(([cis, nist, att, p, t, tk, a, prop]) => {
      setCisCard(cis.data || null)
      setNistCard(nist.data || null)
      setAttestQueue(att.data?.items || att.data?.queue || [])
      setPurges(p.data?.pending ?? p.data?.count ?? (Array.isArray(p.data) ? p.data.length : 0))
      setCampaigns(att.data?.campaigns?.length ?? att.data?.active_campaigns ?? 0)
      setDoraTimers(t.data?.timers || t.data?.results || [])
      setTickets(tk.data?.tickets || tk.data?.results || [])
      setAudit(a.data?.events || a.data?.results || [])
      setFindings((cis.data?.failing ?? 0) + (nist.data?.failing ?? 0))
      setProposals(prop.data?.proposals?.length ?? prop.data?.count ?? 0)
    }).finally(() => setRefreshing(false))
  }, [])

  useEffect(() => {
    load()
    const id = setInterval(load, 60_000)
    return () => clearInterval(id)
  }, [load])

  // First DORA-tagged timer countdown — best-effort.
  const dora = doraTimers.find(t => (t.framework || '').toUpperCase().includes('DORA')) || doraTimers[0]
  const daysUntilDora = dora?.due_at
    ? Math.max(0, Math.round((new Date(dora.due_at).getTime() - Date.now()) / 86_400_000))
    : null

  return (
    <div className="space-y-4 animate-fade-in pb-16">
      <LandingHeader
        title="Compliance Landing"
        subtitle="scorecards · attestation · GDPR · audit · regulation"
        persona="compliance"
        onRefresh={load}
        refreshing={refreshing}
      />

      {/* Hero — 6 tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <HeroTile label="CIS-v8" value={cisCard?.percent != null ? `${cisCard.percent.toFixed(0)}%` : '—'}
          tone={cisCard?.percent == null ? 'muted' : cisCard.percent >= 90 ? 'emerald' : cisCard.percent >= 75 ? 'amber' : 'rose'}
          to="/app/cdm/compliance" />
        <HeroTile label="NIST CSF" value={nistCard?.percent != null ? `${nistCard.percent.toFixed(0)}%` : '—'}
          tone={nistCard?.percent == null ? 'muted' : nistCard.percent >= 90 ? 'emerald' : nistCard.percent >= 75 ? 'amber' : 'rose'}
          to="/app/cdm/compliance" />
        <HeroTile label="Open findings" value={safeNum(openFindings)}
          tone={openFindings > 0 ? 'amber' : 'emerald'} to="/app/cdm/compliance" />
        <HeroTile label="My attestations" value={safeNum(attestQueue.length)}
          tone={attestQueue.length > 0 ? 'amber' : 'emerald'} sub="awaiting you" />
        <HeroTile label="Pending GDPR purges" value={safeNum(purgesPending)}
          tone={purgesPending > 0 ? 'amber' : 'emerald'} to="/app/cdm/privacy" />
        <HeroTile label="Days to DORA"
          value={daysUntilDora != null ? `${daysUntilDora}d` : '—'}
          tone={daysUntilDora == null ? 'muted' : daysUntilDora < 7 ? 'rose' : daysUntilDora < 30 ? 'amber' : 'emerald'}
          to="/app/cdm/regulation-timers" sub={dora?.framework} />
      </div>

      <TwoColumnBody
        left={(
          <>
            <Panel title="My attestation items" viewAllTo="/app/cdm/compliance">
              {attestQueue.length === 0 ? (
                <EmptyState message="Your attestation queue is empty."
                  configureTo="/app/cdm/compliance" configureLabel="open compliance" />
              ) : (
                <div className="space-y-1">
                  {attestQueue.slice(0, 5).map(it => (
                    <ListRow key={it.id}
                      primary={it.title || it.id}
                      secondary={it.framework}
                      right={it.due_at ? `due ${new Date(it.due_at).toLocaleDateString()}` : ''} />
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Open compliance auto-tickets" viewAllTo="/app/cdm/remediation">
              {tickets.length === 0 ? (
                <EmptyState message="No open auto-tickets." />
              ) : (
                <div className="space-y-1">
                  {tickets.slice(0, 5).map(t => (
                    <ListRow key={t.id}
                      primary={t.title || t.rule || t.id}
                      secondary={t.framework}
                      right={<span className={t.severity === 'critical' ? 'text-rose-light' : 'text-amber-light'}>
                        {t.severity}
                      </span>} />
                  ))}
                </div>
              )}
            </Panel>
          </>
        )}
        right={(
          <>
            <Panel title="ChangeProposal evidence summary" viewAllTo="/app/cdm/changes">
              <div className="text-center py-2 text-[11px]">
                <span className="font-mono text-lg font-bold text-amber-light">{proposals}</span>
                <div className="text-text-muted">awaiting evidence review</div>
                <div className="text-[10px] text-text-muted mt-1">{campaigns} active campaigns</div>
              </div>
            </Panel>

            <Panel title="Recent audit events" viewAllTo="/app/activity">
              {audit.length === 0 ? (
                <EmptyState message="No recent audit activity captured." />
              ) : (
                <div className="space-y-1">
                  {audit.slice(0, 5).map(e => (
                    <ListRow key={e.id}
                      primary={e.action}
                      secondary={e.actor}
                      right={e.created_at?.slice(0, 16)} />
                  ))}
                </div>
              )}
            </Panel>
          </>
        )}
      />

      <StickyFooter actions={[
        { label: 'Open scorecard',       icon: Scale,            tone: 'primary',   to: '/app/cdm/compliance' },
        { label: 'Approve attestation',  icon: ClipboardCheck,   tone: 'secondary', to: '/app/cdm/compliance' },
        { label: 'Run reconciliation',   icon: RefreshCw,        tone: 'ghost',     to: '/app/cdm/adapters' },
        { label: 'Change pipeline',      icon: GitPullRequest,   tone: 'ghost',     to: '/app/cdm/changes' },
      ]} />
    </div>
  )
}
